import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import { Settings } from 'lucide-react';

interface NavbarProps {
  onSearchClick?: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ onSearchClick }) => {
  const { isAuthenticated, isAdmin, user, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { t } = useTranslation();
  const { language, setLanguage } = useLanguage();

  return (
    <nav className="sticky top-0 z-50 nav-glass">
      <div className="flex justify-between items-center px-4 md:px-margin-desktop py-4 max-w-container mx-auto">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img src="/logo.png" alt="Meettix" className="h-14 w-auto" />
        </Link>

        {/* Desktop Nav links */}
        <div className="hidden md:flex items-center gap-8">
          <Link to="/" className="font-body text-body-md text-on-surface-variant hover:text-primary transition-colors">
            {t('nav.home')}
          </Link>
          <Link to="/events" className="font-body text-body-md text-on-surface-variant hover:text-primary transition-colors">
            {t('nav.events')}
          </Link>
          <Link to="/past-events" className="font-body text-body-md text-on-surface-variant hover:text-primary transition-colors">
            {t('nav.pastEvents')}
          </Link>
        </div>

        {/* Right side */}
        <div className="hidden md:flex items-center gap-4">
          {onSearchClick && (
            <button
              onClick={onSearchClick}
              className="p-2 text-on-surface-variant hover:text-primary transition-colors"
              aria-label="Buscar"
            >
              <span className="material-symbols-outlined">search</span>
            </button>
          )}

          {/* Language toggle */}
          <button
            onClick={() => setLanguage(language === 'pt' ? 'en' : 'pt')}
            className="font-body text-label-md text-on-surface-variant hover:text-primary transition-colors px-2"
          >
            {language === 'pt' ? '🇺🇸 EN' : '🇧🇷 PT'}
          </button>

          {isAuthenticated ? (
            <div className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-2 p-1 rounded-full hover:bg-surface-container-low transition-colors"
              >
                {user?.photoURL ? (
                  <img src={user.photoURL} alt={user.name} className="w-8 h-8 rounded-full object-cover border-2 border-primary" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-primary-fixed flex items-center justify-center border-2 border-primary">
                    <span className="text-primary text-label-md font-bold">{user?.name?.charAt(0).toUpperCase()}</span>
                  </div>
                )}
                <span className="material-symbols-outlined text-on-surface-variant text-sm">expand_more</span>
              </button>

              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-surface-container-lowest rounded-xl card-elevation border border-outline-variant py-2 z-10">
                  <div className="px-4 py-3 border-b border-outline-variant">
                    <p className="text-label-md font-bold text-on-surface">{user?.name}</p>
                    <p className="text-label-sm text-on-surface-variant">{user?.email}</p>
                  </div>
                  {isAdmin && (
                    <Link
                      to="/admin"
                      className="flex items-center gap-3 px-4 py-2 text-on-surface hover:bg-surface-container-low transition-colors"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <Settings className="h-4 w-4" />
                      <span className="text-body-md">{t('nav.adminDashboard')}</span>
                    </Link>
                  )}
                  <Link
                    to="/profile"
                    className="flex items-center gap-3 px-4 py-2 text-on-surface hover:bg-surface-container-low transition-colors"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    <span className="material-symbols-outlined text-[18px]">person</span>
                    <span className="text-body-md">{t('nav.profile')}</span>
                  </Link>
                  <button
                    onClick={() => { logout(); setIsDropdownOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2 text-on-surface hover:bg-surface-container-low transition-colors"
                  >
                    <span className="material-symbols-outlined text-[18px]">logout</span>
                    <span className="text-body-md">{t('nav.signOut')}</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Link to="/login" className="font-body text-body-md text-on-surface-variant hover:text-primary transition-colors">
                {t('nav.login')}
              </Link>
              <Link
                to="/register"
                className="btn-primary px-6 py-2"
              >
                {t('nav.register')}
              </Link>
            </div>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-full hover:bg-surface-container-high transition-colors"
        >
          <span className="material-symbols-outlined text-primary">{isMenuOpen ? 'close' : 'menu'}</span>
        </button>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden border-t border-outline-variant bg-surface px-4 py-4 space-y-3">
          <Link to="/" className="block py-2 text-on-surface-variant hover:text-primary font-body text-body-md transition-colors" onClick={() => setIsMenuOpen(false)}>
            {t('nav.home')}
          </Link>
          <Link to="/events" className="block py-2 text-on-surface-variant hover:text-primary font-body text-body-md transition-colors" onClick={() => setIsMenuOpen(false)}>
            {t('nav.events')}
          </Link>
          <Link to="/past-events" className="block py-2 text-on-surface-variant hover:text-primary font-body text-body-md transition-colors" onClick={() => setIsMenuOpen(false)}>
            {t('nav.pastEvents')}
          </Link>
          {isAuthenticated ? (
            <div className="pt-3 border-t border-outline-variant space-y-3">
              {isAdmin && (
                <Link to="/admin" className="flex items-center gap-2 py-2 text-on-surface hover:text-primary transition-colors" onClick={() => setIsMenuOpen(false)}>
                  <Settings className="h-4 w-4" />
                  <span className="text-body-md">{t('nav.adminDashboard')}</span>
                </Link>
              )}
              <Link to="/profile" className="flex items-center gap-2 py-2 text-on-surface hover:text-primary transition-colors" onClick={() => setIsMenuOpen(false)}>
                <span className="material-symbols-outlined text-[18px]">person</span>
                <span className="text-body-md">{t('nav.profile')}</span>
              </Link>
              <button onClick={() => { logout(); setIsMenuOpen(false); }} className="flex items-center gap-2 py-2 text-on-surface hover:text-primary transition-colors">
                <span className="material-symbols-outlined text-[18px]">logout</span>
                <span className="text-body-md">{t('nav.signOut')}</span>
              </button>
            </div>
          ) : (
            <div className="pt-3 border-t border-outline-variant space-y-3">
              <Link to="/login" className="block py-2 text-on-surface-variant hover:text-primary font-body text-body-md transition-colors" onClick={() => setIsMenuOpen(false)}>
                {t('nav.login')}
              </Link>
              <Link to="/register" className="block w-full bg-primary text-on-primary px-6 py-3 rounded-lg font-headline text-label-md text-center hover:brightness-110 transition-all" onClick={() => setIsMenuOpen(false)}>
                {t('nav.register')}
              </Link>
            </div>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
